import type { NotionMember } from "@seagames/contracts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../components/shell/icons";
import { initials } from "../../components/shell/profile-menu";
import { Button } from "../../components/ui/button";
import { Spinner } from "../../components/ui/spinner";
import { api } from "../../lib/api/client";
import { formatDateTime, formatShort } from "../../lib/i18n/format";

/**
 * Who is in the workspace: every ACTIVE login holding the Super Admin role.
 * Read-only here — membership IS the role, granted and removed in GMS
 * (Users), so there is one place to manage it.
 */
export const Route = createFileRoute("/_app/members")({
	component: Members,
});

function Members() {
	const { t } = useTranslation("pages");
	const [text, setText] = useState("");
	const [search, setSearch] = useState("");
	useEffect(() => {
		const id = window.setTimeout(() => setSearch(text.trim()), 250);
		return () => window.clearTimeout(id);
	}, [text]);

	const q = useInfiniteQuery({
		queryKey: ["notion", "members-page", search],
		queryFn: ({ pageParam, signal }) =>
			api<{ items: NotionMember[]; next_cursor: string | null }>("GET", "/notion/members", { query: { search: search || undefined, cursor: pageParam ?? undefined, limit: 50 }, signal }),
		initialPageParam: null as string | null,
		getNextPageParam: (last) => last.next_cursor,
	});
	const items = q.data?.pages.flatMap((p) => p.items) ?? [];

	return (
		<div className="mx-auto w-full max-w-[52rem] px-[1.5rem] pt-[3rem] pb-[4rem] sm:px-[3rem]">
			<h1 className="flex items-center gap-[0.625rem] font-black text-[2rem] text-ink">
				<Icon name="users" className="size-[1.75rem] text-muted" />
				{t("members.title")}
			</h1>
			<p className="mt-[0.25rem] mb-[1.25rem] max-w-[60ch] text-muted">{t("members.subtitle")}</p>
			<input
				value={text}
				onChange={(e) => setText(e.target.value)}
				placeholder={t("members.search")}
				aria-label={t("members.search")}
				className="mb-[1rem] min-h-[2.5rem] w-full max-w-[22rem] rounded-ctl border border-rule bg-sheet px-[0.75rem] text-[0.9375rem] focus:border-board"
			/>
			{q.isPending ? (
				<Spinner className="size-[1.25rem] text-muted" />
			) : q.isError ? (
				<button type="button" onClick={() => void q.refetch()} className="cursor-pointer text-conflict underline">
					{t("members.loadError")}
				</button>
			) : items.length === 0 ? (
				<p className="text-muted">{t("members.empty")}</p>
			) : (
				<div className="overflow-x-auto rounded-card border border-rule">
					<table className="w-full text-left text-[0.875rem]">
						<thead className="bg-paper text-[0.75rem] text-muted">
							<tr>
								<th className="px-[1rem] py-[0.5rem] font-semibold">{t("members.name")}</th>
								<th className="px-[1rem] py-[0.5rem] font-semibold">{t("members.username")}</th>
								<th className="px-[1rem] py-[0.5rem] font-semibold">{t("members.email")}</th>
								<th className="px-[1rem] py-[0.5rem] font-semibold">{t("members.lastSignIn")}</th>
							</tr>
						</thead>
						<tbody>
							{items.map((m) => (
								<tr key={m.id} className="border-rule border-t">
									<td className="px-[1rem] py-[0.625rem]">
										<span className="flex items-center gap-[0.5rem]">
											<span aria-hidden="true" className="flex size-[1.75rem] shrink-0 items-center justify-center rounded-full bg-board font-bold text-[0.625rem] text-white">
												{initials(m.name)}
											</span>
											<span className="font-semibold">{m.name}</span>
										</span>
									</td>
									<td className="px-[1rem] py-[0.625rem] font-mono text-[0.8125rem]">{m.username}</td>
									<td className="px-[1rem] py-[0.625rem] text-muted">{m.email ?? "—"}</td>
									<td className="px-[1rem] py-[0.625rem] text-muted" title={m.last_login_at ? formatDateTime(m.last_login_at) : undefined}>
										{m.last_login_at ? formatShort(m.last_login_at) : t("members.never")}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{q.hasNextPage && (
				<Button variant="outline" size="sm" className="mt-[0.75rem]" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
					{t("members.more")}
				</Button>
			)}
		</div>
	);
}
